// Shared progress bar — supports read-only display and optional click-to-edit mode
// Sizes: sm (h-1.5) for thin indicators, md (h-2) for standard bars

import { useState } from 'react';

export interface ProgressBarProps {
  value: number; // 0-100, clamped internally
  size?: 'sm' | 'md';
  color?: string; // Inline CSS color (dynamic, e.g. status-based)
  colorClass?: string; // Tailwind fill class or gradient. Default: brand blue-to-red gradient
  trackClass?: string; // Tailwind class for the track background. Default: bg-base-200
  label?: string;
  showPercent?: boolean;
  editable?: boolean;
  onChange?: (value: number) => void;
  className?: string;
}

const SIZE_CLASSES = { sm: 'h-1.5', md: 'h-2' } as const;

export function ProgressBar({
  value,
  size = 'md',
  color,
  colorClass,
  trackClass = 'bg-base-200',
  label,
  showPercent = false,
  editable = false,
  onChange,
  className = '',
}: ProgressBarProps) {
  const [editing, setEditing] = useState(false);
  const clamped = Math.max(0, Math.min(100, value));
  const h = SIZE_CLASSES[size];

  // Fill color: inline style takes priority, then colorClass, then brand gradient default
  const fillClass = colorClass ?? (color ? '' : 'bg-gradient-to-r from-[#19A2F8] to-red-600');
  const fillStyle = color ? { backgroundColor: color } : undefined;

  return (
    <div className={`flex items-center gap-3 px-1 ${className}`}>
      {label && <span className="text-sm text-base-content/60 min-w-[120px]">{label}</span>}

      <div className="flex-1 flex items-center gap-2">
        {editing ? (
          // Range slider for editing
          <input
            type="range"
            min={0}
            max={100}
            value={clamped}
            onChange={(e) => onChange?.(Number(e.target.value))}
            onBlur={() => setEditing(false)}
            className="range range-sm range-primary flex-1"
            autoFocus
          />
        ) : (
          // Visual bar
          <div
            className={`flex-1 ${trackClass} rounded-full overflow-hidden ${h} ${editable ? 'cursor-pointer' : ''}`}
            onClick={() => editable && setEditing(true)}
            role={editable ? 'button' : undefined}
            tabIndex={editable ? 0 : undefined}
            onKeyDown={(e) => editable && e.key === 'Enter' && setEditing(true)}
          >
            <div
              className={`${fillClass} rounded-full ${h} transition-all`}
              style={{ width: `${clamped}%`, ...fillStyle }}
            />
          </div>
        )}

        {showPercent && (
          <span className="text-sm font-medium text-primary min-w-[36px] text-right">
            {clamped}%
          </span>
        )}
      </div>
    </div>
  );
}
